'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useState } from 'react';
import { decompressFrames, parseGIF } from 'gifuct-js';
import { Pause, Play } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ContentfulImage } from '@/components/contentful-image';
interface GifPlayerProps {
  src: string;
  alt?: string;
  className?: string;
}
function useFirstFrame(gifUrl: string) {
  const [firstFrame, setFirstFrame] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!gifUrl) return;
    async function fetchFirstFrame(url: string) {
      setIsLoading(true);
      setError(null);
      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error('Failed to fetch GIF');
        const buffer = await res.arrayBuffer();
        const gif = parseGIF(buffer);
        const frames = decompressFrames(gif, true);
        if (!frames?.length) throw new Error('No frames found in GIF');
        const frame = frames[0];
        const canvas = document.createElement('canvas');
        canvas.width = frame.dims.width;
        canvas.height = frame.dims.height;
        const ctx = canvas.getContext('2d');
        if (!ctx || !frame.patch) throw new Error('Failed to process GIF frame');
        const imageData = ctx.createImageData(frame.dims.width, frame.dims.height);
        imageData.data.set(frame.patch);
        ctx.putImageData(imageData, 0, 0);
        setFirstFrame(canvas.toDataURL());
      } catch (err: any) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    }
    fetchFirstFrame(gifUrl);
  }, [gifUrl]);
  return {
    firstFrame,
    isLoading,
    error
  };
}
export function GifPlayer(t0) {
  const $ = _c(40);
  const {
    src,
    alt: t1,
    className
  } = t0;
  const alt = t1 === undefined ? "" : t1;
  const {
    firstFrame,
    isLoading,
    error
  } = useFirstFrame(src);
  const [isPaused, setIsPaused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setIsPaused(_temp);
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const handleToggle = t2;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = e => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        handleToggle();
      }
    };
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const handleKeyDown = t3;
  if (error) {
    let t4;
    if ($[2] !== className) {
      t4 = cn("relative mb-4 aspect-video w-full", className);
      $[2] = className;
      $[3] = t4;
    } else {
      t4 = $[3];
    }
    let t5;
    if ($[4] !== alt || $[5] !== src) {
      t5 = <ContentfulImage alt={alt} className="m-0 rounded-lg object-cover" src={src} fill={true} />;
      $[4] = alt;
      $[5] = src;
      $[6] = t5;
    } else {
      t5 = $[6];
    }
    let t6;
    if ($[7] !== t4 || $[8] !== t5) {
      t6 = <div className={t4}>{t5}</div>;
      $[7] = t4;
      $[8] = t5;
      $[9] = t6;
    } else {
      t6 = $[9];
    }
    return t6;
  }
  const displaySrc = isPaused && firstFrame ? firstFrame : src;
  const Icon = isPaused ? Play : Pause;
  const buttonLabel = isPaused ? "Play GIF" : "Pause GIF";
  let t4;
  if ($[10] !== className) {
    t4 = cn("relative mb-4 aspect-video w-full", className);
    $[10] = className;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  let t6;
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = cn("group relative block size-full overflow-hidden rounded-lg", "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accessibility-ring focus-visible:ring-offset-2", "transition-all duration-200 ease-in-out");
    t6 = () => setIsHovered(true);
    t7 = () => setIsHovered(false);
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
  } else {
    t5 = $[12];
    t6 = $[13];
    t7 = $[14];
  }
  const t8 = !isPaused;
  const t9 = isPaused && firstFrame ? displaySrc : src;
  let t10;
  if ($[15] !== alt || $[16] !== t9) {
    t10 = <ContentfulImage alt={alt} className="m-0 object-cover" src={t9} fill={true} />;
    $[15] = alt;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  const t11 = isHovered || isPaused ? "opacity-100" : "opacity-0";
  let t12;
  if ($[18] !== t11) {
    t12 = cn("absolute inset-0 flex items-center justify-center transition-opacity duration-300", t11);
    $[18] = t11;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  const t13 = isLoading && "animate-pulse";
  let t14;
  if ($[20] !== t13) {
    t14 = cn("flex size-8 items-center justify-center rounded-full bg-black/60 text-white", "transition-all duration-200 ease-in-out", "group-hover:scale-110 group-focus-visible:scale-110", t13);
    $[20] = t13;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  let t15;
  if ($[22] !== Icon || $[23] !== isLoading) {
    t15 = isLoading ? <div className="size-5 animate-spin rounded-full border-2 border-white border-t-transparent" /> : <Icon className="size-5" />;
    $[22] = Icon;
    $[23] = isLoading;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] !== t14 || $[26] !== t15) {
    t16 = <div className={t14}>{t15}</div>;
    $[25] = t14;
    $[26] = t15;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] !== t12 || $[29] !== t16) {
    t17 = <div className={t12}>{t16}</div>;
    $[28] = t12;
    $[29] = t16;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  let t18;
  if ($[31] !== buttonLabel || $[32] !== isLoading || $[33] !== t10 || $[34] !== t17 || $[35] !== t8) {
    t18 = <button type="button" className={t5} onMouseEnter={t6} onMouseLeave={t7} onClick={handleToggle} onKeyDown={handleKeyDown} aria-label={buttonLabel} aria-pressed={t8} disabled={isLoading}>{t10}{t17}</button>;
    $[31] = buttonLabel;
    $[32] = isLoading;
    $[33] = t10;
    $[34] = t17;
    $[35] = t8;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  let t19;
  if ($[37] !== t18 || $[38] !== t4) {
    t19 = <div className={t4}>{t18}</div>;
    $[37] = t18;
    $[38] = t4;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  return t19;
}
function _temp(prev) {
  return !prev;
}